import { takeEvery } from 'redux-saga';
import { call } from 'redux-saga/effects';

import Validator from 'validatorjs';
import {
  ALL_AUDIO_RULES,
  UPLOADED_IMAGE_RULES,
  UPLOADED_AUDIO_RULES,
} from 'reducers/validation';
import {
  UPLOADED_AUDIO,
  FILE_PROPERTIES,
} from 'config';
import * as actions from 'actions/uploader';
import * as properties from 'config/uploadProperties';
import {
  createPlaceholder,
  uploadFileToStorage,
} from 'api/adCreation';
import debug from 'debug';

const log = debug('sagas:coverArtUploader');

export function fileIsValid(fileProperties, validationKey, { partial = false } = {}) {
  let rules;
  const fileIsImage = UPLOADED_IMAGE_RULES[validationKey];
  const fileIsAudio = ALL_AUDIO_RULES[validationKey];

  if (fileIsAudio) {
    if (partial && validationKey === `uploads.${UPLOADED_AUDIO}.${FILE_PROPERTIES}`) {
      rules = {
        [validationKey]: UPLOADED_AUDIO_RULES[validationKey]
          .replace('audio-duration', 'noop'),
      };
    }
    rules = { [validationKey]: ALL_AUDIO_RULES[validationKey] };
  } else if (fileIsImage) {
    if (partial) {
      rules = {
        [validationKey]: UPLOADED_IMAGE_RULES[validationKey]
          .replace('image-dimensions', 'noop'),
      };
    } else {
      rules = { [validationKey]: UPLOADED_IMAGE_RULES[validationKey] };
    }
  }
  const validator = new Validator({ [validationKey]: fileProperties }, rules);
  validator.check();
  return validator.errorCount === 0;
}

export function buildFileProperties(action, fileProperties) {
  const { payload: { file } } = action;

  if (!file) {
    // Some browser extensions can mess with inputs in
    // interesting ways. If a change event gets triggered
    // without a file, we don't want this saga to break.
    fileProperties[properties.FILE_FAIL] = true;
  }

  fileProperties[properties.FILE_TYPE_PROP] = file.type;
  fileProperties[properties.FILE_SIZE_PROP] = file.size;

  return fileProperties;
}

export function getDataUri(file) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.addEventListener('load', () => {
      resolve(fileReader.result);
    });
    fileReader.addEventListener('error', (err) => {
      reject(err);
    });
    fileReader.readAsDataURL(file);
  });
}

// Refactor this to be more generic with it's elements
export function getImageFromUri(uri) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.onerror = (err) => {
      reject(err);
    };
    img.src = uri;
  });
}

export function getAudioFromUri(uri) {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.oncanplaythrough = () => {
      resolve(audio);
    };
    audio.onerror = (err) => {
      reject(err);
    };
    audio.src = uri;
  });
}

export function* buildImageFileProperties(action, fileProperties) {
  const {
    payload: {
      file,
      validationKey,
    },
  } = action;
  buildFileProperties(action, fileProperties);

  if (fileIsValid(fileProperties, validationKey, { partial: true })) {
    let imageUri;

    try {
      imageUri = yield call(getDataUri, file);
    } catch (err) {
      log('File read did not work: ' + err);
      fileProperties[properties.FILE_FAIL] = true;
    }

    let image;
    try {
      image = yield call(getImageFromUri, imageUri);
    } catch (err) {
      log('Image load did not work: ' + err);
      fileProperties[properties.FILE_FAIL] = true;
    }

    image = image || {
      naturalWidth: 0,
      naturalHeight: 0,
    };

    fileProperties[properties.IMAGE_DIMENSIONS] = `${image.naturalWidth}x${image.naturalHeight}`;
  }

  return fileProperties;
}

export function* buildAudioFileProperties(action, fileProperties) {
  const {
    payload: {
      file,
      validationKey,
    },
  } = action;
  buildFileProperties(action, fileProperties);

  if (fileIsValid(fileProperties, validationKey, { partial: true })) {
    let audioUri;
    try {
      audioUri = yield call(getDataUri, file);
    } catch (err) {
      log('File read did not work: ' + err);
      fileProperties[properties.FILE_FAIL] = true;
    }

    let audio;
    try {
      audio = yield call(getAudioFromUri, audioUri);
    } catch (err) {
      log('Image load did not work: ' + err);
      fileProperties[properties.FILE_FAIL] = true;
    }

    audio = audio || {
      duration: NaN,
    };

    fileProperties[properties.AUDIO_DURATION] = audio.duration;

    return fileProperties;
  }
}

export function buildFormData(file, placeholderData) {
  const { fields } = placeholderData;

  const formData = new FormData();
  Object.keys(fields).forEach(k => formData.append(k, fields[k]));

  formData.append('Content-Type', file.type);
  formData.append('file', file, file.name);

  return {
    formData,
    placeholderData,
  };
}

export function* uploadFile(action, fileProperties) {
  const {
    payload: {
      file,
      placeholderType,
      wgToken,
      onComplete,
      onProgress,
      onError,
    },
  } = action;

  const placeholderData = yield call(createPlaceholder, placeholderType, file.type, wgToken);
  const res = buildFormData(file, placeholderData);

  try {
    yield call(uploadFileToStorage,
      {
        url: res.placeholderData.action,
        formData: res.formData,
      },
      onProgress
    );
  } catch (err) {
    onError(err, fileProperties);
  }

  onComplete(res.placeholderData, fileProperties);
}

export function* validateAndUploadFile(action) {
  const {
    type,
    payload: {
      onError,
      validationKey,
    },
  } = action;
  let fileProperties;

  if (type === actions.AUDIO_FILE_SELECTED) {
    fileProperties = yield call(buildAudioFileProperties,
      action,
      properties.createDefaultAudioProperties(),
    );
  } else if (type === actions.IMAGE_FILE_SELECTED) {
    fileProperties = yield call(buildImageFileProperties,
      action,
      properties.createDefaultImageProperties(),
    );
  }

  if (fileIsValid(fileProperties, validationKey)) {
    yield call(uploadFile, action, fileProperties);
  } else {
    onError('invalid', fileProperties);
    log(fileProperties);
  }
}

export function* watchFileSelected() {
  yield takeEvery([
    actions.IMAGE_FILE_SELECTED,
    actions.AUDIO_FILE_SELECTED,
  ], validateAndUploadFile);
}



// WEBPACK FOOTER //
// ./src/middleware/sagas/uploader.js